import { isGameSoundOn } from '../sound';
import type { PoolSoundEvent } from './physics';

/**
 * Table sounds, synthesised with WebAudio — no files, nothing fetched (north-star #7). The
 * cue strike, ball-on-ball clicks, cushion thuds and the pocket thunk are all short shaped
 * bursts of noise and sine; loudness follows the physics' closing speed. Obeys the games-wide
 * sound switch (`isGameSoundOn`, localStorage `awwyeah.games.sound`) on every single call,
 * so flipping it mid-shot silences the rest of the shot. Best effort everywhere: no audio
 * context, a blocked one or an exception just means silence.
 *
 * Own `AudioContext` rather than the shared one in `../sound.ts` (which doesn't export it):
 * one more context is cheap, and it keeps this file from reaching into another module's
 * internals. `unlockPoolAudio` must run inside a user gesture for iOS to allow it.
 */

let ctx: AudioContext | null = null;
let noise: AudioBuffer | null = null;
let lastClickAt = 0;

function audio(): AudioContext | null {
  try {
    if (typeof window === 'undefined') return null;
    if (!ctx) {
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return null;
      ctx = new Ctx();
    }
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx.state === 'running' ? ctx : null;
  } catch {
    return null;
  }
}

/** Call from a touch so the context is allowed to make sound later. */
export function unlockPoolAudio(): void {
  audio();
}

function noiseBuffer(ac: AudioContext): AudioBuffer {
  if (noise && noise.sampleRate === ac.sampleRate) return noise;
  const length = Math.floor(ac.sampleRate * 0.25);
  const buffer = ac.createBuffer(1, length, ac.sampleRate);
  const data = buffer.getChannelData(0);
  // A fixed-seed LCG — the sound is decoration, but there's no reason for it to be random.
  let seed = 1;
  for (let i = 0; i < length; i += 1) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    data[i] = seed / 2147483648 - 1;
  }
  noise = buffer;
  return buffer;
}

function burst(
  ac: AudioContext,
  opts: { freq: number; q: number; peak: number; length: number; type?: BiquadFilterType },
): void {
  const src = ac.createBufferSource();
  src.buffer = noiseBuffer(ac);
  const filter = ac.createBiquadFilter();
  filter.type = opts.type ?? 'bandpass';
  filter.frequency.value = opts.freq;
  filter.Q.value = opts.q;
  const gain = ac.createGain();
  const t = ac.currentTime;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, opts.peak), t + 0.002);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + opts.length);
  src.connect(filter);
  filter.connect(gain);
  gain.connect(ac.destination);
  src.start(t);
  src.stop(t + opts.length + 0.02);
}

function thump(
  ac: AudioContext,
  opts: { from: number; to: number; peak: number; length: number; delay?: number },
): void {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = 'sine';
  const t = ac.currentTime + (opts.delay ?? 0);
  osc.frequency.setValueAtTime(opts.from, t);
  osc.frequency.exponentialRampToValueAtTime(opts.to, t + opts.length);
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, opts.peak), t + 0.006);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + opts.length);
  osc.connect(gain);
  gain.connect(ac.destination);
  osc.start(t);
  osc.stop(t + opts.length + 0.02);
}

function play(fn: (ac: AudioContext) => void): void {
  if (!isGameSoundOn()) return;
  const ac = audio();
  if (!ac) return;
  try {
    fn(ac);
  } catch {
    // never load-bearing
  }
}

/** The tip meeting the cue ball: a woody tock, harder with power. */
export function playCueStrike(power: number): void {
  play((ac) => {
    const p = Math.min(1, Math.max(0.05, power));
    burst(ac, { freq: 1800, q: 1.4, peak: 0.12 + 0.25 * p, length: 0.05 });
    thump(ac, { from: 260, to: 140, peak: 0.08 + 0.18 * p, length: 0.07 });
  });
}

/** One physics event from the shot's playback. */
export function playTableEvent(event: PoolSoundEvent): void {
  play((ac) => {
    const level = Math.min(1, event.speed / 6);
    if (event.kind === 'ball') {
      // A break is fifteen clicks in a few ms — let the first few through and skip the rest.
      const now = ac.currentTime;
      if (now - lastClickAt < 0.018) return;
      lastClickAt = now;
      burst(ac, { freq: 3200 + level * 1400, q: 3, peak: 0.05 + 0.3 * level, length: 0.035 });
      thump(ac, { from: 2400, to: 1900, peak: 0.03 + 0.12 * level, length: 0.03 });
    } else if (event.kind === 'cushion') {
      if (level < 0.04) return;
      burst(ac, { freq: 380, q: 0.9, peak: 0.04 + 0.16 * level, length: 0.09, type: 'lowpass' });
      thump(ac, { from: 150, to: 90, peak: 0.04 + 0.12 * level, length: 0.1 });
    } else {
      // Pocket: a hollow drop and a little rattle in the leather.
      thump(ac, { from: 140, to: 55, peak: 0.28, length: 0.22 });
      burst(ac, { freq: 900, q: 2, peak: 0.06, length: 0.12 });
      thump(ac, { from: 900, to: 700, peak: 0.04, length: 0.04, delay: 0.09 });
    }
  });
}
